import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { RootState, StoreDispatch } from '../../store/configure-store'
import { newHttpService } from '../../services/new-http-service'
import { notifyDropdownOpen, useCachedFetch } from '../../hooks/use-cached-fetch'
import { SelectCompanyPrompt } from '../../cmps/general/select-company-prompt'
import { Spinner } from '../../cmps/util-cmps/spinner'
import { SlideDrawer } from '../../cmps/general/slide-drawer'
import { setUserMsg } from '../../store/user'
import { getEntitiesByCompanyNameQuery } from '../../cmps/util-cmps/graphql-queries/entities-query'
import { getEntitySystemsByCompanyNameQuery } from '../../cmps/util-cmps/graphql-queries/entity-systems-query'

type PageMode = 'rule-manager' | 'etl'
type EditorKind = 'etl' | 'rule'

interface EditorState {
    kind: EditorKind
    mode?: 'edit' | 'delete'
    row: any
    param?: any
    bucket?: string
    objective?: string
    rule?: string
    scope?: string
    scopes?: string[]
    scopeEntries?: Array<{ scope: string; value: string }>
    value?: string
    listItems?: string[]
    listInput?: string
    active?: boolean
    params?: string
    deleteReason?: string
    deleteSummary?: string
    deleteDescription?: string
}

interface RuleScopeOption {
    value: string
    label: string
}

const ALL_SYSTEMS_SCOPE = '*'
const getDeleteCustomizationLabel = (count: number) => (count > 1 ? 'Delete Customizations' : 'Delete Customization')

const readErrorMessage = async (error: any) => {
    if (!error) return ''
    if (typeof error === 'string') return error
    if (error instanceof Error) return error.message
    if (typeof error?.message === 'string') return error.message
    if (typeof error?.response?.data === 'string') return error.response.data
    if (typeof error?.response?.data?.message === 'string') return error.response.data.message
    if (typeof error?.response?.data?.error === 'string') return error.response.data.error
    if (Array.isArray(error?.response?.data?.errors) && error.response.data.errors[0]?.message) return error.response.data.errors[0].message
    if (typeof error?.text === 'function') {
        try {
            const text = await error.text()
            return text || ''
        } catch {
            return ''
        }
    }
    return ''
}

const parseJson = (value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return {}
    return JSON.parse(trimmed)
}

const cloneJson = (value: any) => JSON.parse(JSON.stringify(value ?? {}))

const prettyJson = (value: any) => JSON.stringify(value ?? {}, null, 2)

const prettyJsonForEditor = (value: any) => {
    if (typeof value === 'string') {
        try {
            return prettyJson(JSON.parse(value))
        } catch {
            return value
        }
    }
    return prettyJson(value)
}

const normalizeRuleParamsObject = (value: any) => {
    if (Array.isArray(value)) {
        return value.reduce((acc: Record<string, any>, item: any) => {
            if (!item || typeof item !== 'object' || !item.name) return acc
            const { name, ...rest } = item
            acc[name] = normalizeRuleParamsObject(unwrapRuleParamValue(rest))
            return acc
        }, {})
    }
    if (value && typeof value === 'object') {
        if (isRuleParamMetadataObject(value)) {
            return unwrapRuleParamValue(value)
        }
        return Object.entries(value).reduce((acc: Record<string, any>, [key, item]) => {
            acc[key] = normalizeRuleParamsObject(unwrapRuleParamValue(item))
            return acc
        }, {})
    }
    return value
}

const isRuleParamMetadataObject = (value: any) => (
    !!value
    && typeof value === 'object'
    && !Array.isArray(value)
    && (
        'default' in value
        || 'effective' in value
        || 'override' in value
        || 'has_override' in value
        || 'description' in value
    )
)

const unwrapRuleParamValue = (value: any) => {
    if (!isRuleParamMetadataObject(value)) return value
    if (value.override !== null && value.override !== undefined) return value.override
    if (value.effective !== undefined) return value.effective
    if (value.default !== undefined) return value.default
    return null
}

const validateRuleParamValue = (paramName: string, value: any, type: string | undefined) => {
    if (!type) return

    const normalizedType = type.trim().toLowerCase()
    const unwrappedValue = unwrapRuleParamValue(value)

    if (normalizedType === 'int') {
        if (typeof unwrappedValue === 'string') {
            const trimmed = unwrappedValue.trim()
            if (trimmed) {
                throw new Error(
                    `${paramName} is an integer field. Enter a raw number such as 60, not a quoted string or the metadata object returned by the API.`
                )
            }
        }
        if (typeof unwrappedValue !== 'number' || !Number.isInteger(unwrappedValue)) {
            throw new Error(`${paramName} is an integer field. Enter a raw number such as 60.`)
        }
        return
    }

    if (normalizedType === 'bool') {
        if (typeof unwrappedValue !== 'boolean') {
            throw new Error(`${paramName} must be true or false`)
        }
        return
    }

    if (normalizedType === 'list[str]') {
        if (!Array.isArray(unwrappedValue) || unwrappedValue.some(item => typeof item !== 'string')) {
            throw new Error(`${paramName} must be an array of strings`)
        }
        return
    }

    if (normalizedType === 'dict[str,str]') {
        if (
            !unwrappedValue
            || typeof unwrappedValue !== 'object'
            || Array.isArray(unwrappedValue)
            || Object.entries(unwrappedValue).some(([key, item]) => typeof key !== 'string' || typeof item !== 'string')
        ) {
            throw new Error(`${paramName} must be an object of string values`)
        }
    }
}

const getRuleParamTypes = (row: any) => {
    const paramTypes = new Map<string, string | undefined>()
    const rowParams = Array.isArray(row?.params) ? row.params : []

    rowParams.forEach((param: any) => {
        if (!param || typeof param !== 'object' || !param.name) return
        paramTypes.set(param.name, param.type)
    })

    return paramTypes
}

const getRuleParamDefaults = (row: any) => {
    const defaults: Record<string, any> = {}
    const rowParams = Array.isArray(row?.params) ? row.params : []

    rowParams.forEach((param: any) => {
        if (!param || typeof param !== 'object' || !param.name) return
        defaults[param.name] = normalizeRuleParamsObject(unwrapRuleParamValue(param.default))
    })

    return defaults
}

const getRuleParamDefinition = (row: any) => {
    const param = row?.param && typeof row.param === 'object' ? row.param : null
    if (param?.name) return param
    return null
}

const formatRuleParamEditorValue = (value: any) => {
    if (value === undefined || value === null) return ''
    if (typeof value === 'string') return value
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    return prettyJson(value)
}

export const resolveRuleParamEditorValue = (current: any, row: any) => {
    const param = getRuleParamDefinition(row)
    if (!param?.name) return ''

    const currentParams = normalizeRuleParamsEditorCandidate(
        current?.params
        ?? current?.params_json
        ?? current?.customization_json
        ?? current?.override_json
    )
    const currentValue = currentParams && typeof currentParams === 'object' && !Array.isArray(currentParams)
        ? unwrapRuleParamValue(currentParams[param.name])
        : undefined
    const fallbackValue = normalizeRuleParamsObject(unwrapRuleParamValue(param.default))
    return formatRuleParamEditorValue(currentValue !== undefined ? currentValue : fallbackValue)
}

const getRuleOverrideForScope = (objective: string, row: any, scope: string, ruleOverrideRoot: Record<string, any>) => {
    return ruleOverrideRoot?.[objective]?.[scope]?.[row.rule]
        || row.override_scopes?.[scope]?.[row.rule]
        || {}
}

export const resolveRuleParamScopeEditorState = (
    objective: string,
    row: any,
    param: any,
    ruleOverrideRoot: Record<string, any>,
    scopeOverride?: string,
) => {
    const rootScopes = Object.keys(ruleOverrideRoot?.[objective] || {})
    const snapshotScopes = Object.keys(row?.override_scopes || {})
    const scopeNames = Array.from(new Set([...rootScopes, ...snapshotScopes]))
    const normalizedScopes = normalizeRuleScopeSelection(scopeNames.filter(scope => !!getRuleOverrideForScope(objective, row, scope, ruleOverrideRoot)))
    const systemScopes = normalizedScopes.filter(scope => scope !== ALL_SYSTEMS_SCOPE)
    const selectedScope = scopeOverride && scopeOverride !== ALL_SYSTEMS_SCOPE ? scopeOverride : null
    const orderedSystemScopes = selectedScope
        ? Array.from(new Set([selectedScope, ...systemScopes])).filter(Boolean)
        : systemScopes

    const fallbackOverride = getRuleOverrideForScope(objective, row, ALL_SYSTEMS_SCOPE, ruleOverrideRoot)
    const fallbackValue = resolveRuleParamEditorValue(fallbackOverride, { ...row, param })

    return {
        fallbackValue,
        scopeEntries: orderedSystemScopes.map(scope => ({
            scope,
            value: resolveRuleParamEditorValue(getRuleOverrideForScope(objective, row, scope, ruleOverrideRoot), { ...row, param }),
        })),
    }
}

export const parseRuleParamEditorValue = (row: any, value: string) => {
    const param = getRuleParamDefinition(row)
    const rawValue = value.trim()
    if (!param?.type) return rawValue

    const normalizedType = String(param.type).trim().toLowerCase()

    if (normalizedType === 'int') {
        if (!rawValue) return rawValue
        const parsed = Number(rawValue)
        return parsed
    }

    if (normalizedType === 'bool') {
        const lower = rawValue.toLowerCase()
        if (lower === 'true') return true
        if (lower === 'false') return false
        return rawValue
    }

    if (normalizedType === 'list[str]') {
        if (!rawValue) return []
        if (rawValue.startsWith('[')) {
            try {
                const parsed = JSON.parse(rawValue)
                if (Array.isArray(parsed)) return parsed.map(item => String(item))
            } catch {
                // fall through to newline/comma parsing
            }
        }
        return rawValue
            .split(/\n|,/)
            .map(item => item.trim())
            .filter(Boolean)
    }

    if (normalizedType === 'dict[str,str]' || normalizedType === 'json') {
        if (!rawValue) return {}
        return JSON.parse(rawValue)
    }

    return rawValue
}

const getRuleParamEditorControl = (
    type: string | undefined,
    value: string,
    onChange: (nextValue: string) => void,
    placeholder?: string,
) => {
    const normalizedType = String(type || '').trim().toLowerCase()
    if (normalizedType === 'int') {
        return (
            <input
                type="number"
                inputMode="numeric"
                step="1"
                value={value}
                onChange={event => onChange(event.target.value)}
                placeholder={placeholder}
                className="customizations-field__input--compact"
            />
        )
    }
    if (normalizedType === 'bool') {
        return (
            <select value={value} onChange={event => onChange(event.target.value)}>
                <option value="true">true</option>
                <option value="false">false</option>
            </select>
        )
    }
    return (
        <textarea
            value={value}
            onChange={event => onChange(event.target.value)}
            rows={2}
            className="customizations-field__textarea--compact"
            placeholder={placeholder}
        />
    )
}

const normalizeRuleParamsEditorCandidate = (value: any) => {
    if (typeof value === 'string') {
        const trimmed = value.trim()
        if (!trimmed) return {}
        try {
            return normalizeRuleParamsObject(JSON.parse(trimmed))
        } catch {
            return {}
        }
    }

    return normalizeRuleParamsObject(value)
}

export const validateRuleParamsForSave = (row: any, params: any) => {
    const parsedParams = params && typeof params === 'object' && !Array.isArray(params) ? params : {}
    const paramTypes = getRuleParamTypes(row)

    Object.entries(parsedParams).forEach(([paramName, paramValue]) => {
        validateRuleParamValue(paramName, paramValue, paramTypes.get(paramName))
    })
}

export const normalizeRuleParamsForSave = (row: any, params: any) => {
    const parsedParams = params && typeof params === 'object' && !Array.isArray(params) ? params : {}
    return Object.entries(parsedParams).reduce((acc: Record<string, any>, [paramName, paramValue]) => {
        acc[paramName] = normalizeRuleParamsObject(unwrapRuleParamValue(paramValue))
        return acc
    }, {})
}

export const mergeRuleParamsForSave = (row: any, currentParams: any, params: any) => {
    const existingParams = normalizeRuleParamsObject(currentParams)
    const baseParams = {
        ...getRuleParamDefaults(row),
        ...(existingParams && typeof existingParams === 'object' && !Array.isArray(existingParams) ? existingParams : {}),
    }
    const normalizedParams = normalizeRuleParamsForSave(row, params)

    return {
        ...baseParams,
        ...normalizedParams,
    }
}

export const isFlowSuppressionRule = (row: any) => typeof row?.rule === 'string' && row.rule.endsWith('Flow')

export const isParameterRule = (row: any) => typeof row?.rule === 'string' && row.rule.endsWith('Rule')

export const splitRuleRowsBySection = (rows: any[]) => ({
    flowSuppressions: rows.filter(isFlowSuppressionRule),
    parameters: rows.filter(isParameterRule),
})

export const resolveRuleEditorScopes = (row: any, scopeOverride?: string) => {
    if (scopeOverride) {
        return normalizeRuleScopeSelection([scopeOverride])
    }

    const scopes = Object.keys(row?.override_scopes || {})
    return normalizeRuleScopeSelection(scopes.length ? scopes : [ALL_SYSTEMS_SCOPE])
}

export const resolveRuleRowScopeEntries = (
    objective: string,
    row: any,
    ruleOverrideRoot: Record<string, any>,
) => {
    const rootScopes = ruleOverrideRoot?.[objective] || {}
    const snapshotScopes = row?.override_scopes || {}
    const scopeNames = Array.from(new Set([
        ...Object.keys(rootScopes || {}),
        ...Object.keys(snapshotScopes || {}),
    ]))

    const scopes = scopeNames.filter(scopeName => rootScopes?.[scopeName]?.[row.rule] || snapshotScopes?.[scopeName]?.[row.rule])
    const orderedScopes = normalizeRuleScopeSelection(scopes.length ? scopes : [ALL_SYSTEMS_SCOPE])

    return orderedScopes.map(scope => ({
        scope,
        override: rootScopes?.[scope]?.[row.rule] || snapshotScopes?.[scope]?.[row.rule] || null,
    }))
}

export const buildParameterTableRows = (
    rows: any[],
    objective: string,
    getRuleRowScopeEntriesFn: (objective: string, row: any) => { scope: string; override: any }[],
    getScopeLabelFn: (scope: string) => string,
) => (
    rows.flatMap((row: any) => {
        if (!isParameterRule(row)) return []
        const rowParams = Array.isArray(row.params) ? row.params : []

        return getRuleRowScopeEntriesFn(objective, row).flatMap(({ scope, override }) => {
            const scopeLabel = getScopeLabelFn(scope)
            const overrideParams = override?.params && typeof override.params === 'object' ? normalizeRuleParamsObject(override.params) : {}

            return rowParams.map((param: any) => {
                const overrideValue = normalizeRuleParamsObject(overrideParams?.[param.name])
                const hasOverride = overrideValue !== undefined && overrideValue !== null
                const defaultValue = normalizeRuleParamsObject(param.default)

                return {
                    key: `${objective}:${row.rule}.${param.name}:${scopeLabel}`,
                    rule: row.rule,
                    paramName: param.name,
                    description: param.description,
                    type: param.type,
                    defaultValue,
                    overrideValue: hasOverride ? overrideValue : null,
                    hasOverride,
                    scopeLabel,
                    scope,
                    row,
                    param,
                }
            })
        })
    })
)

export const resolveRuleParamsEditorValue = (current: any, row: any) => {
    const editorParams = {
        ...getRuleParamDefaults(row),
    }
    const savedParams = normalizeRuleParamsEditorCandidate(
        current?.params
        ?? current?.params_json
        ?? current?.customization_json
        ?? current?.override_json
    )

    if (savedParams && typeof savedParams === 'object' && !Array.isArray(savedParams)) {
        return prettyJsonForEditor({
            ...editorParams,
            ...savedParams,
        })
    }

    return prettyJsonForEditor(editorParams)
}

const formatValue = (value: any) => {
    if (value === null || value === undefined || value === '') return '—'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
}

const isListType = (value: string | undefined) => !!value && value.startsWith('list')

const normalizeListItems = (value: any) => {
    if (Array.isArray(value)) return value.map(item => String(item)).filter(Boolean)
    if (value === null || value === undefined || value === '') return []
    return [String(value)]
}

const isPlainListItem = (value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return false
    if (/^['"].*['"]$/.test(trimmed)) return false
    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) return false
    return true
}

const bucketLabel = (value: string) => value.replace(/_/g, ' ')

const normalizeSystemKey = (value: string | null | undefined) => {
    if (!value) return ''
    return value
        .trim()
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
}

const isNormalizedSystemKey = (value: string) => /^[a-z0-9]+(?:_[a-z0-9]+)*$/.test(value)

export const normalizeRuleScopeSelection = (scopes: string[]) => {
    const cleaned = scopes
        .map(scope => scope.trim())
        .filter(Boolean)
        .map(scope => (scope === ALL_SYSTEMS_SCOPE ? ALL_SYSTEMS_SCOPE : normalizeSystemKey(scope)))
        .filter(Boolean)
    return Array.from(new Set(cleaned)).sort((left, right) => left.localeCompare(right))
}

export const buildRuleOverridePayload = (objective: string, rule: string, scopes: string[], active: boolean, params: any) => {
    const nextOverrides: Record<string, Record<string, Record<string, { active: boolean; params: any }>>> = {}
    const nextScopes = normalizeRuleScopeSelection(scopes)
    nextOverrides[objective] = {}
    nextScopes.forEach(scope => {
        nextOverrides[objective][scope] = nextOverrides[objective][scope] || {}
        nextOverrides[objective][scope][rule] = { active, params }
    })
    return nextOverrides
}

export const mergeRuleOverridePayload = (
    existingOverrides: Record<string, any>,
    objective: string,
    rule: string,
    scopes: string[],
    active: boolean,
    params: any,
) => {
    const nextOverrides = cloneJson(existingOverrides)
    const nextScopes = normalizeRuleScopeSelection(scopes)
    const objectiveOverrides = nextOverrides[objective] && typeof nextOverrides[objective] === 'object'
        ? nextOverrides[objective]
        : {}

    const nextRulePayload = buildRuleOverridePayload(objective, rule, nextScopes, active, params)[objective]
    Object.entries(nextRulePayload || {}).forEach(([scope, scopeRules]) => {
        objectiveOverrides[scope] = {
            ...(objectiveOverrides[scope] || {}),
            ...scopeRules,
        }
    })

    nextOverrides[objective] = objectiveOverrides
    return nextOverrides
}

export const mergeExclusiveRuleOverridePayload = (
    existingOverrides: Record<string, any>,
    objective: string,
    rule: string,
    scopes: string[],
    active: boolean,
    params: any,
) => {
    const nextOverrides = cloneJson(existingOverrides)
    const nextScopes = normalizeRuleScopeSelection(scopes)
    const objectiveOverrides = nextOverrides[objective] && typeof nextOverrides[objective] === 'object'
        ? nextOverrides[objective]
        : {}

    Object.keys(objectiveOverrides).forEach(scope => {
        if (nextScopes.includes(scope)) return
        const scopeRules = objectiveOverrides[scope]
        if (scopeRules && scopeRules[rule]) {
            delete scopeRules[rule]
            if (!Object.keys(scopeRules).length) {
                delete objectiveOverrides[scope]
            }
        }
    })

    const nextRulePayload = buildRuleOverridePayload(objective, rule, nextScopes, active, params)[objective]
    Object.entries(nextRulePayload || {}).forEach(([scope, scopeRules]) => {
        objectiveOverrides[scope] = {
            ...(objectiveOverrides[scope] || {}),
            ...scopeRules,
        }
    })

    nextOverrides[objective] = objectiveOverrides
    return nextOverrides
}

export const buildFlowSuppressionTogglePayload = (
    ruleOverrideRoot: Record<string, any>,
    objective: string,
    row: any,
    nextActive: boolean,
) => {
    const selectedScopes = normalizeRuleScopeSelection(Object.keys(row?.override_scopes || {}))
    const scopes = selectedScopes.length ? selectedScopes : [ALL_SYSTEMS_SCOPE]
    const currentScope = scopes[0] || ALL_SYSTEMS_SCOPE
    const currentOverride = getRuleOverrideForScope(objective, row, currentScope, ruleOverrideRoot)

    return mergeExclusiveRuleOverridePayload(
        cloneJson(ruleOverrideRoot),
        objective,
        row.rule,
        scopes,
        nextActive,
        currentOverride?.params || {},
    )
}

const compactParams = (params: Record<string, any>) =>
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''))

const IconPencil = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
)

const getEditDisabledMessage = (isDisabled: boolean, entityLabel: string, s3Message: string) => {
    if (!isDisabled) return 'Edit override'
    return entityLabel || s3Message
}

const isS3DefaultRow = (row: any) => row?.has_override === false || row?.customization_source === 's3' || row?.customization_source === 'default'
const getS3EditTooltip = (isDisabled: boolean, disabledMessage: string) =>
    isDisabled ? (disabledMessage || 'This row is using the S3 default. Create a customization before editing.') : null

const mergeRowsByKey = (baseRows: any[] = [], overrideRows: any[] = []) => {
    const rowsByKey = new Map<string, any>()
    baseRows.forEach((row: any) => {
        if (row?.key !== undefined && row?.key !== null) {
            rowsByKey.set(String(row.key), row)
        }
    })
    overrideRows.forEach((row: any) => {
        if (row?.key === undefined || row?.key === null) return
        const rowKey = String(row.key)
        rowsByKey.set(rowKey, {
            ...(rowsByKey.get(rowKey) || {}),
            ...row,
        })
    })
    return Array.from(rowsByKey.values())
}

const mergeObjectivesByKey = (baseBuckets: Record<string, any[]> = {}, overrideBuckets: Record<string, any[]> = {}) => {
    const mergedBuckets: Record<string, any[]> = { ...baseBuckets }
    Object.entries(overrideBuckets || {}).forEach(([bucket, overrideRows]) => {
        mergedBuckets[bucket] = mergeRowsByKey(baseBuckets[bucket] || [], overrideRows || [])
    })
    return mergedBuckets
}

interface AvailableSystemOption {
    key: string
    label: string
    defaults_key?: string | null
}

interface DropdownOption {
    id: string
    label: string
    subtitle?: string
    isCustomized: boolean
}

interface CustomizationsDropdownProps {
    label: string
    value: string
    placeholder: string
    options: DropdownOption[]
    onChange: (value: string) => void
    disabled?: boolean
    isLoading?: boolean
    dropdownRefreshScope?: string
}

const CustomizationsDropdown: React.FC<CustomizationsDropdownProps> = ({
    label,
    value,
    placeholder,
    options,
    onChange,
    disabled = false,
    isLoading = false,
    dropdownRefreshScope,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchText, setSearchText] = useState('')
    const wrapperRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const optionRefs = useRef<Array<HTMLElement | null>>([])

    const selectedOption = useMemo(
        () => options.find(option => option.id === value) || null,
        [options, value]
    )
    const visibleOptions = useMemo(() => {
        const normalizedSearch = searchText.trim().toLowerCase()
        const sortedOptions = [...options].sort((left, right) => left.label.localeCompare(right.label))
        if (!normalizedSearch) return sortedOptions
        return sortedOptions.filter(option => {
            const haystack = `${option.label} ${option.subtitle || ''}`.toLowerCase()
            return haystack.includes(normalizedSearch)
        })
    }, [options, searchText])

    useEffect(() => {
        if (!isOpen) return
        const handleMouseDown = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false)
                triggerRef.current?.focus()
            }
        }
        document.addEventListener('mousedown', handleMouseDown)
        document.addEventListener('keydown', handleEscape)
        return () => {
            document.removeEventListener('mousedown', handleMouseDown)
            document.removeEventListener('keydown', handleEscape)
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return
        const selectedIndex = Math.max(visibleOptions.findIndex(option => option.id === value), 0)
        requestAnimationFrame(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus()
                searchInputRef.current.select()
            } else {
                optionRefs.current[selectedIndex]?.focus()
            }
        })
    }, [isOpen, visibleOptions, value])

    const openMenu = () => {
        if (!disabled) {
            setSearchText('')
            notifyDropdownOpen(dropdownRefreshScope)
            setIsOpen(true)
        }
    }

    const closeMenu = () => {
        setIsOpen(false)
        setSearchText('')
    }

    const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (disabled) return
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openMenu()
        }
    }

    const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLElement>, index: number) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            optionRefs.current[Math.min(index + 1, visibleOptions.length - 1)]?.focus()
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            optionRefs.current[Math.max(index - 1, 0)]?.focus()
        } else if (event.key === 'Home') {
            event.preventDefault()
            optionRefs.current[0]?.focus()
        } else if (event.key === 'End') {
            event.preventDefault()
            optionRefs.current[visibleOptions.length - 1]?.focus()
        } else if (event.key === 'Escape') {
            event.preventDefault()
            closeMenu()
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onChange(visibleOptions[index].id)
            closeMenu()
        }
    }

    return (
        <div className={`customizations-dropdown${disabled ? ' is-disabled' : ''}`} ref={wrapperRef}>
            <span className="customizations-filter-label">{label}</span>
            <button
                ref={triggerRef}
                type="button"
                className={`customizations-dropdown__trigger${isOpen ? ' is-open' : ''}${!selectedOption ? ' is-placeholder' : ''}`}
                onClick={() => (isOpen ? closeMenu() : openMenu())}
                onKeyDown={handleTriggerKeyDown}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={`${label}: ${selectedOption?.label || placeholder}`}
            >
                <span className="customizations-dropdown__triggerText">
                    {selectedOption?.label || placeholder}
                </span>
                {selectedOption?.isCustomized && (
                    <span className="customizations-source-pill is-database">Customized</span>
                )}
                <svg className="customizations-dropdown__chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {isOpen && (
                <div className="customizations-dropdown__menu" role="listbox" aria-label={label}>
                    <div className="customizations-dropdown__search">
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchText}
                            onChange={event => setSearchText(event.target.value)}
                            placeholder="Search.."
                            aria-label={`Search ${label}`}
                        />
                    </div>
                    {isLoading ? (
                        <div className="customizations-dropdown__loading">
                            <Spinner isSmall />
                        </div>
                    ) : visibleOptions.length === 0 ? (
                        <div className="customizations-dropdown__empty">
                            {searchText.trim() ? 'No matches' : 'No options available'}
                        </div>
                    ) : (
                        visibleOptions.map((option, index) => (
                            <div
                                key={option.id}
                                ref={el => { optionRefs.current[index] = el }}
                                role="option"
                                tabIndex={0}
                                aria-selected={option.id === value}
                                className={`customizations-dropdown__option${option.id === value ? ' is-selected' : ''}${option.isCustomized ? ' is-customized' : ''}`}
                                onClick={event => {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    onChange(option.id)
                                    closeMenu()
                                }}
                                onKeyDown={event => handleOptionKeyDown(event, index)}
                            >
                                <span className="customizations-dropdown__optionMain">
                                    <span className="customizations-dropdown__optionLabel">{option.label}</span>
                                    {option.subtitle && <span className="customizations-dropdown__optionSubtitle">{option.subtitle}</span>}
                                </span>
                                {option.isCustomized && (
                                    <span className="customizations-source-pill is-database">Customized</span>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    )
}

interface RuleScopeMultiSelectProps {
    label: string
    placeholder: string
    options: RuleScopeOption[]
    value: string[]
    onChange: (scopes: string[]) => void
    dropdownRefreshScope?: string
}

export const RuleScopeMultiSelect: React.FC<RuleScopeMultiSelectProps> = ({
    label,
    placeholder,
    options,
    value,
    onChange,
    dropdownRefreshScope,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchText, setSearchText] = useState('')
    const wrapperRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)

    const selectedScopes = useMemo(() => normalizeRuleScopeSelection(value), [value])
    const visibleOptions = useMemo(() => {
        const normalizedSearch = searchText.trim().toLowerCase()
        const sortedOptions = [...options].sort((left, right) => left.label.localeCompare(right.label))
        if (!normalizedSearch) return sortedOptions
        return sortedOptions.filter(option => {
            const haystack = `${option.label} ${option.value}`.toLowerCase()
            return haystack.includes(normalizedSearch)
        })
    }, [options, searchText])

    useEffect(() => {
        if (!isOpen) return
        const handleMouseDown = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false)
                triggerRef.current?.focus()
            }
        }
        document.addEventListener('mousedown', handleMouseDown)
        document.addEventListener('keydown', handleEscape)
        return () => {
            document.removeEventListener('mousedown', handleMouseDown)
            document.removeEventListener('keydown', handleEscape)
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return
        requestAnimationFrame(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus()
                searchInputRef.current.select()
            }
        })
    }, [isOpen])

    const closeMenu = () => {
        setIsOpen(false)
        setSearchText('')
    }

    const toggleScope = (scope: string) => {
        const nextScopes = normalizeRuleScopeSelection(selectedScopes)
        if (nextScopes.includes(ALL_SYSTEMS_SCOPE)) {
            const confirmed = window.confirm(
                'Switching from All systems (*) to a specific system will update only that system for this rule. Continue?'
            )
            if (!confirmed) return
            onChange([scope])
            closeMenu()
            return
        }
        const updatedScopes = nextScopes.includes(scope)
            ? nextScopes.filter(item => item !== scope)
            : [...nextScopes, scope]
        onChange(normalizeRuleScopeSelection(updatedScopes))
        closeMenu()
    }

    const handleAllSystemsChange = () => {
        const isSelected = selectedScopes.includes(ALL_SYSTEMS_SCOPE)
        if (isSelected) {
            onChange([])
            return
        }
        if (selectedScopes.length > 0) {
            const confirmed = window.confirm('Selecting all systems will clear the selected systems. Continue?')
            if (!confirmed) return
        }
        onChange([ALL_SYSTEMS_SCOPE])
    }

    const removeScope = (scope: string) => {
        if (scope === ALL_SYSTEMS_SCOPE) {
            onChange([])
            return
        }
        onChange(normalizeRuleScopeSelection(selectedScopes.filter(item => item !== scope)))
    }

    const chips = selectedScopes
        .filter(scope => scope !== ALL_SYSTEMS_SCOPE)
        .map(scope => ({
        scope,
        label: options.find(option => option.value === scope)?.label || scope,
    }))

    return (
        <div className="customizations-scope-dropdown" ref={wrapperRef}>
            <span className="customizations-filter-label">{label}</span>
            <label className="customizations-scope-dropdown__all-systems">
                <input
                    type="checkbox"
                    checked={selectedScopes.includes(ALL_SYSTEMS_SCOPE)}
                    onChange={handleAllSystemsChange}
                />
                <span>All systems (*)</span>
            </label>
            <button
                ref={triggerRef}
                type="button"
                className={`customizations-dropdown__trigger${isOpen ? ' is-open' : ''}${selectedScopes.length === 0 ? ' is-placeholder' : ''}`}
                onClick={() => {
                    if (isOpen) {
                        closeMenu()
                        return
                    }
                    notifyDropdownOpen(dropdownRefreshScope)
                    setIsOpen(true)
                }}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={`${label}: ${selectedScopes.length ? chips.map(chip => chip.label).join(', ') : placeholder}`}
            >
                <span className="customizations-dropdown__triggerText">
                    {selectedScopes.length ? `${selectedScopes.length} selected` : placeholder}
                </span>
                <svg className="customizations-dropdown__chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {!!chips.length && (
                <div className="customizations-scope-dropdown__chips" aria-label={`${label} selections`}>
                    {chips.map(chip => (
                        <span key={chip.scope} className="customizations-chip customizations-chip--removable">
                            <span>{chip.label}</span>
                            <button type="button" onClick={() => removeScope(chip.scope)} aria-label={`Remove ${chip.label}`}>
                                ×
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {isOpen && (
                <div className="customizations-dropdown__menu" role="listbox" aria-label={label}>
                    <div className="customizations-dropdown__search">
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchText}
                            onChange={event => setSearchText(event.target.value)}
                            placeholder="Search.."
                            aria-label={`Search ${label}`}
                        />
                    </div>
                    <div className="customizations-scope-dropdown__list">
                        {visibleOptions.map(option => (
                            <button
                                key={option.value}
                                type="button"
                                className={`customizations-scope-dropdown__option${selectedScopes.includes(option.value) ? ' is-selected' : ''}`}
                                onClick={() => toggleScope(option.value)}
                            >
                                <span>{option.label}</span>
                                {selectedScopes.includes(option.value) && <span aria-hidden="true">✓</span>}
                            </button>
                        ))}
                    </div>
                    <div className="customizations-dropdown__empty">
                        Choose one or more systems. Use the checkbox above to switch to all systems.
                    </div>
                </div>
            )}
        </div>
    )
}

export const CustomizationsPage: React.FC = () => {
    const dispatch = useDispatch<StoreDispatch>()
    const companyName = useSelector((state: RootState) => state.companyName.value)
    const location = useLocation()
    const navigate = useNavigate()
    const didInitCompanyRef = useRef(false)
    const [selectedBuckets, setSelectedBuckets] = useState<string[]>([])
    const [selectedObjectives, setSelectedObjectives] = useState<string[]>([])
    const [editor, setEditor] = useState<EditorState | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [editorError, setEditorError] = useState('')
    const [etlCustomizationOverrides, setEtlCustomizationOverrides] = useState<Record<string, boolean>>({})

    const pageMode: PageMode = location.pathname.includes('/etl-customizations') ? 'etl' : 'rule-manager'
    const isEtlPage = pageMode === 'etl'

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
    const selectedSystemKey = useMemo(() => {
        const initial = searchParams.get('system_key')
        return initial ? normalizeSystemKey(initial) : null
    }, [searchParams])
    const selectedEntityId = useMemo(() => {
        const initial = searchParams.get('entity_id')
        if (!initial) return null
        const parsed = Number(initial)
        return Number.isNaN(parsed) ? null : parsed
    }, [searchParams])

    const syncSelectionToSearchParams = useCallback((nextSystemKey: string | null, nextEntityId: number | null) => {
        const next = new URLSearchParams(location.search)
        const desiredSystemKey = nextSystemKey !== null ? normalizeSystemKey(nextSystemKey) : null
        const desiredEntityId = nextEntityId !== null ? String(nextEntityId) : null

        if (desiredSystemKey) next.set('system_key', desiredSystemKey)
        else next.delete('system_key')

        if (desiredEntityId) next.set('entity_id', desiredEntityId)
        else next.delete('entity_id')

        navigate(
            {
                pathname: location.pathname,
                search: next.toString() ? `?${next.toString()}` : '',
            },
            { replace: true }
        )
    }, [location.pathname, location.search, navigate])

    const clearSelectionSearchParams = useCallback(() => {
        navigate(
            {
                pathname: location.pathname,
                search: '',
            },
            { replace: true }
        )
    }, [location.pathname, navigate])

    useEffect(() => {
        if (!didInitCompanyRef.current) {
            didInitCompanyRef.current = true
            return
        }
        setSelectedBuckets([])
        setSelectedObjectives([])
        setEditor(null)
        clearSelectionSearchParams()
    }, [companyName, clearSelectionSearchParams])

    useEffect(() => {
        setSelectedBuckets([])
        setSelectedObjectives([])
        setEditor(null)
    }, [pageMode])

    useEffect(() => {
        setEtlCustomizationOverrides({})
    }, [companyName, pageMode])

    const { data: entitiesData, isLoading: isEntitiesLoading, hasError: hasEntitiesError, refetch: refetchEntities } = useCachedFetch<any>(
        companyName ? `customizations-entities:${companyName}` : null,
        async () => {
            const result = await newHttpService.graphql(companyName, getEntitiesByCompanyNameQuery(companyName))
            return result.entitiesByCompanyName || []
        },
        { refreshOnDropdownOpen: 'customizations-page' }
    )

    const { data: entitySystemsData, isLoading: isEntitySystemsLoading, refetch: refetchEntitySystems } = useCachedFetch<any>(
        companyName ? `customizations-entity-systems:${companyName}` : null,
        async () => {
            const result = await newHttpService.graphql(companyName, getEntitySystemsByCompanyNameQuery(companyName))
            return result.entitySystemsByCompanyName || []
        },
        { refreshOnDropdownOpen: 'customizations-page' }
    )

    const { data: etlMetaData, isLoading: isEtlMetaLoading } = useCachedFetch<any>(
        companyName && isEtlPage ? `customizations-etl-meta:${companyName}` : null,
        async () => {
            return newHttpService.get('/api/backoffice/customizations/etl', {
                company_name: companyName,
            })
        },
        { refreshOnDropdownOpen: 'customizations-page' }
    )

    const entities = useMemo<any[]>(() => entitiesData || [], [entitiesData])
    const entitySystems = useMemo<any[]>(() => entitySystemsData || [], [entitySystemsData])
    const fallbackSystems = useMemo<AvailableSystemOption[]>(() => {
        const systemsByKey = new Map<string, AvailableSystemOption>()
        entitySystems.forEach((entitySystem: any) => {
            const systemName = entitySystem.system?.name || entitySystem.system?.abbreviation || ''
            const systemKey = normalizeSystemKey(systemName)
            if (!systemKey || systemsByKey.has(systemKey)) return
            systemsByKey.set(systemKey, {
                key: systemKey,
                label: systemName || systemKey,
                defaults_key: entitySystem.system?.defaults_key ?? null,
            })
        })
        return Array.from(systemsByKey.values()).sort((left, right) => left.label.localeCompare(right.label))
    }, [entitySystems])
    const availableSystems = useMemo<AvailableSystemOption[]>(
        () => (etlMetaData?.available_systems?.length ? etlMetaData.available_systems : fallbackSystems),
        [etlMetaData, fallbackSystems]
    )
    const systemOptions = useMemo<DropdownOption[]>(
        () => availableSystems.map((system: AvailableSystemOption) => ({
            id: system.key,
            label: system.label,
            isCustomized: false,
        })),
        [availableSystems]
    )
    const selectedSystem = useMemo(
        () => availableSystems.find((system: AvailableSystemOption) => system.key === selectedSystemKey) || null,
        [availableSystems, selectedSystemKey]
    )
    const selectedEntity = useMemo(
        () => entities.find((entity: any) => Number(entity.id) === Number(selectedEntityId)) || null,
        [entities, selectedEntityId]
    )
    const selectedRuleEntitySystems = useMemo(
        () => !isEtlPage && selectedEntityId !== null
            ? entitySystems.filter((entitySystem: any) => Number(entitySystem.entity?.id) === Number(selectedEntityId))
            : [],
        [entitySystems, isEtlPage, selectedEntityId]
    )
    const selectedEntitySystems = useMemo(
        () => isEtlPage && selectedSystemKey !== null
            ? entitySystems.filter((entitySystem: any) =>
                normalizeSystemKey(entitySystem.system?.name || entitySystem.system?.abbreviation || '') === selectedSystemKey
            )
            : [],
        [entitySystems, isEtlPage, selectedSystemKey]
    )
    const selectedEntitySystem = useMemo(
        () => selectedEntitySystems.find((entitySystem: any) => Number(entitySystem.entity?.id) === Number(selectedEntityId)) || null,
        [selectedEntityId, selectedEntitySystems]
    )
    const etlSelectionParams = useMemo(() => {
        if (!selectedSystemKey || !selectedEntity) return {}
        return selectedEntitySystem
            ? { entity_system_id: selectedEntitySystem.id }
            : { entity_name: selectedEntity.name, system_key: selectedSystemKey }
    }, [selectedEntity, selectedEntitySystem, selectedSystemKey])
    const etlEntityOptions = useMemo<DropdownOption[]>(
        () => {
            const uniqueEntities = new Map<string, any>()
            selectedEntitySystems.forEach((entitySystem: any) => {
                const entity = entitySystem.entity
                if (entity && !uniqueEntities.has(String(entity.id))) {
                    uniqueEntities.set(String(entity.id), entity)
                }
            })
            return Array.from(uniqueEntities.values())
                .map((entity: any) => {
                    const matchingSystems = selectedEntitySystems.filter((entitySystem: any) =>
                        Number(entitySystem.entity?.id) === Number(entity.id)
                    )
                    return {
                        id: String(entity.id),
                        label: entity.name,
                        subtitle: undefined,
                        isCustomized: matchingSystems.some((entitySystem: any) =>
                            entitySystem.hasEtlCustomization || etlCustomizationOverrides[String(entitySystem.id)]
                        ),
                    }
                })
                .sort((left, right) => left.label.localeCompare(right.label))
        },
        [etlCustomizationOverrides, selectedEntitySystems]
    )

    const ruleEntityOptions = useMemo<DropdownOption[]>(
        () => [...entities]
            .map((entity: any) => ({
                id: String(entity.id),
                label: entity.name,
                subtitle: undefined,
                isCustomized: !!entity.hasRuleCustomization,
            }))
            .sort((left, right) => left.label.localeCompare(right.label)),
        [entities]
    )

    const ruleScopeOptions = useMemo<RuleScopeOption[]>(() => {
        const systemOptions = new Map<string, RuleScopeOption>()
        selectedRuleEntitySystems.forEach((entitySystem: any) => {
            const systemName = entitySystem.system?.name || entitySystem.system?.abbreviation || ''
            const systemKey = normalizeSystemKey(systemName)
            if (!systemKey || systemOptions.has(systemKey)) return
            systemOptions.set(systemKey, {
                value: systemKey,
                label: systemName || systemKey,
            })
        })
        return Array.from(systemOptions.values()).sort((left, right) => left.label.localeCompare(right.label))
    }, [selectedRuleEntitySystems])
    const ruleScopeOptionValues = useMemo(() => new Set(ruleScopeOptions.map(option => option.value)), [ruleScopeOptions])
    const ruleScopeLabelByValue = useMemo(() => {
        const labels = new Map<string, string>()
        ruleScopeOptions.forEach(option => {
            labels.set(option.value, option.label)
        })
        return labels
    }, [ruleScopeOptions])
    const getRuleScopeLabel = useCallback((scope: string) => {
        if (scope === ALL_SYSTEMS_SCOPE) return 'All systems (*)'
        return ruleScopeLabelByValue.get(scope) || scope
    }, [ruleScopeLabelByValue])
    const entityOptions = isEtlPage ? etlEntityOptions : ruleEntityOptions

    const etlDefaultsCacheKey = companyName && isEtlPage && selectedSystemKey !== null
        ? `customizations-etl-defaults:${companyName}:${selectedSystemKey}`
        : null
    const etlOverrideCacheKey = companyName && isEtlPage && selectedEntityId !== null
        ? `customizations-etl-override:${companyName}:${selectedSystemKey}:${selectedEntityId}`
        : null

    const { data: etlDefaultsData, isLoading: isEtlDefaultsLoading, hasError: hasEtlDefaultsError } = useCachedFetch<any>(
        etlDefaultsCacheKey,
        async () => {
            return newHttpService.get('/api/backoffice/customizations/etl', compactParams({
                company_name: companyName,
                system_key: selectedSystemKey,
            }))
        }
    )

    const { data: etlOverrideData, isLoading: isEtlOverrideLoading, hasError: hasEtlOverrideError, refetch: refetchEtlOverride } = useCachedFetch<any>(
        etlOverrideCacheKey,
        async () => {
            return newHttpService.get('/api/backoffice/customizations/etl', compactParams({
                company_name: companyName,
                system_key: selectedSystemKey,
                ...etlSelectionParams,
            }))
        }
    )

    const { data: ruleData, isLoading: isRuleLoading, hasError: hasRuleError, refetch: refetchRule } = useCachedFetch<any>(
        companyName && !isEtlPage ? `customizations-rule:${companyName}:${selectedEntity?.id ?? 'default'}` : null,
        async () => {
            return newHttpService.get('/api/backoffice/customizations/rule-manager', compactParams({
                company_name: companyName,
                entity_id: selectedEntity?.id,
                entity_name: selectedEntity?.name,
            }))
        }
    )

    const etlData = useMemo(() => {
        if (!etlDefaultsData && !etlOverrideData) return undefined
        if (!etlOverrideData) return etlDefaultsData
        const baseBuckets = etlDefaultsData?.rows_by_bucket || {}
        const overrideBuckets = etlOverrideData?.rows_by_bucket || {}
        return {
            ...(etlDefaultsData || {}),
            ...(etlOverrideData || {}),
            rows_by_bucket: mergeObjectivesByKey(baseBuckets, overrideBuckets),
        }
    }, [etlDefaultsData, etlOverrideData])

    const etlObjectives = useMemo<[string, any[]][]>(() => Object.entries(etlData?.rows_by_bucket || {}) as [string, any[]][], [etlData])
    const ruleObjectives = useMemo<[string, any[]][]>(() => Object.entries(ruleData?.objectives || {}) as [string, any[]][], [ruleData])
    const ruleOverrideRoot = useMemo(() => (
        ruleData?.customization?.overrides
        ?? ruleData?.customization?.customization_json
        ?? ruleData?.customization_json
        ?? ruleData?.override?.overrides
        ?? ruleData?.override?.customization_json
        ?? ruleData?.override
        ?? {}
    ), [ruleData])
    const getRuleRowScopeEntries = useCallback((objective: string, row: any) => (
        resolveRuleRowScopeEntries(objective, row, ruleOverrideRoot)
    ), [ruleOverrideRoot])
    const getRuleRowOverride = useCallback((objective: string, row: any) => {
        const rootScopes = ruleOverrideRoot?.[objective] || {}
        const snapshotScopes = row?.override_scopes || {}
        const scopeNames = [
            ...Object.keys(rootScopes || {}),
            ...Object.keys(snapshotScopes || {}),
        ]
        const selectedScopes = scopeNames.filter(scopeName => rootScopes?.[scopeName]?.[row.rule] || snapshotScopes?.[scopeName]?.[row.rule])
        const scope = selectedScopes[0] || (scopeNames.includes(ALL_SYSTEMS_SCOPE) ? ALL_SYSTEMS_SCOPE : scopeNames[0] || ALL_SYSTEMS_SCOPE)
        return {
            scope,
            scopes: normalizeRuleScopeSelection(selectedScopes.length ? selectedScopes : [scope]),
            override: rootScopes?.[scope]?.[row.rule] || snapshotScopes?.[scope]?.[row.rule] || null,
        }
    }, [ruleOverrideRoot])
    const defaultSourceFileName = (selectedSystem?.defaults_key || 'system.json').replace(/^etls\/systems\//, '')
    const showEtlSourcePill = !!selectedSystem
    const hasSelectedEtlOverride = Boolean(selectedEntitySystem?.hasEtlCustomization || etlData?.customization_source === 'database')
    const etlSourceLabel = hasSelectedEtlOverride ? 'Customization' : `Default from ${defaultSourceFileName}`
    const etlSourceTone = hasSelectedEtlOverride ? 'is-database' : 'is-default'
    const hasSelectedRuleCustomization = Boolean(selectedEntity?.hasRuleCustomization || ruleData?.customization_source === 'database')
    const ruleSourceLabel = hasSelectedRuleCustomization ? 'Customization' : 'Default from Catalog'
    const ruleSourceTone = hasSelectedRuleCustomization ? 'is-database' : 'is-default'
    useEffect(() => {
        if (!etlObjectives.length) return
        setSelectedBuckets(prev => {
            const valid = prev.filter(bucket => etlObjectives.some(([nextBucket]) => nextBucket === bucket))
            if (valid.length) return valid
            return [etlObjectives[0][0] as string]
        })
    }, [etlObjectives])

    useEffect(() => {
        if (!ruleObjectives.length) return
        setSelectedObjectives(prev => {
            const valid = prev.filter(objective => ruleObjectives.some(([nextObjective]) => nextObjective === objective))
            if (valid.length) return valid
            return [ruleObjectives[0][0] as string]
        })
    }, [ruleObjectives])

    useEffect(() => {
        if (!isEtlPage) return
        if (isEtlMetaLoading) return
        if (!availableSystems.length) return
        if (selectedSystemKey === null) return
        const hasSelectedSystem = availableSystems.some(system => system.key === selectedSystemKey)
        if (!hasSelectedSystem) {
            syncSelectionToSearchParams(null, null)
        }
    }, [availableSystems, isEtlMetaLoading, isEtlPage, selectedSystemKey, syncSelectionToSearchParams])

    useEffect(() => {
        if (!isEtlPage) return
        if (selectedSystemKey === null) {
            if (selectedEntityId !== null) {
                syncSelectionToSearchParams(null, null)
            }
            return
        }
        if (!selectedEntitySystems.length) return
        if (selectedEntityId === null) return
        const hasSelectedEntity = selectedEntitySystems.some((entitySystem: any) => Number(entitySystem.entity?.id) === Number(selectedEntityId))
        if (!hasSelectedEntity) {
            syncSelectionToSearchParams(selectedSystemKey, null)
        }
    }, [isEtlPage, selectedEntityId, selectedEntitySystems, selectedSystemKey, syncSelectionToSearchParams])

    const selectedEtlObjectives = useMemo<[string, any[]][]>(() => {
        if (!selectedBuckets.length) return []
        return etlObjectives.filter(([bucket]) => selectedBuckets.includes(bucket))
    }, [etlObjectives, selectedBuckets])
    const toggleEtlBucket = useCallback((bucket: string) => {
        setSelectedBuckets(prev => {
            if (prev.includes(bucket)) {
                if (prev.length === 1) return prev
                return prev.filter(item => item !== bucket)
            }
            return [...prev, bucket]
        })
    }, [])

    const selectedRuleObjectives = useMemo<[string, any[]][]>(() => {
        if (!selectedObjectives.length) return []
        return ruleObjectives.filter(([objective]) => selectedObjectives.includes(objective))
    }, [ruleObjectives, selectedObjectives])
    const toggleRuleObjective = useCallback((objective: string) => {
        setSelectedObjectives(prev => {
            if (prev.includes(objective)) {
                if (prev.length === 1) return prev
                return prev.filter(item => item !== objective)
            }
            return [...prev, objective]
        })
    }, [])
    const renderListChips = useCallback((value: any) => {
        const items = normalizeListItems(value)
        if (!items.length) return '—'
        return (
            <div className="customizations-chip-row" aria-label={Array.isArray(value) ? 'List value' : 'Value list'}>
                {items.map(item => (
                    <span key={item} className="customizations-chip">{item}</span>
                ))}
            </div>
        )
    }, [])

    const renderKeyDescriptionCell = useCallback((key: string, description?: string) => (
        <div className="customizations-key-cell">
            <strong className="customizations-key-cell__title">{key}</strong>
            {description && <span className="customizations-key-cell__description">{description}</span>}
        </div>
    ), [])

    const FlowDescriptionCell = ({ label, description }: { label: string, description?: string }) => {
        const cellRef = useRef<HTMLDivElement | null>(null)
        const [isHovered, setIsHovered] = useState(false)
        const [tooltipPlacement, setTooltipPlacement] = useState<'above' | 'below'>('below')

        const updateTooltipPlacement = useCallback(() => {
            if (!cellRef.current) return
            const rect = cellRef.current.getBoundingClientRect()
            const spaceAbove = rect.top
            const spaceBelow = window.innerHeight - rect.bottom
            setTooltipPlacement(spaceBelow >= spaceAbove ? 'below' : 'above')
        }, [])

        return (
            <div
                ref={cellRef}
                className={`customizations-key-cell${description ? ' customizations-key-cell--hoverable' : ''}`}
                tabIndex={description ? 0 : -1}
                onMouseEnter={() => {
                    if (!description) return
                    updateTooltipPlacement()
                    setIsHovered(true)
                }}
                onMouseLeave={() => setIsHovered(false)}
                onFocus={() => {
                    if (!description) return
                    updateTooltipPlacement()
                    setIsHovered(true)
                }}
                onBlur={() => setIsHovered(false)}
            >
                <strong className="customizations-key-cell__title">{label}</strong>
                {description && isHovered && (
                    <span
                        className={`customizations-key-cell__description customizations-key-cell__description--tooltip customizations-key-cell__description--tooltip-${tooltipPlacement}`}
                        role="tooltip"
                    >
                        {description}
                    </span>
                )}
            </div>
        )
    }

    const renderEtlBucketPanel = (bucket: string, rows: any[]) => {
        return (
            <section key={bucket} className="customizations-panel customizations-panel--compare">
                <div className="customizations-panel__header">
                    <div>
                        <div className="customizations-panel__title-row">
                            <h2>{bucketLabel(bucket)}</h2>
                            {showEtlSourcePill && (
                                <span className={`customizations-source-pill ${etlSourceTone}`}>{etlSourceLabel}</span>
                            )}
                        </div>
                        <p className="customizations-panel__subtitle">{rows.length} keys</p>
                    </div>
                </div>

                <div className="customizations-table-wrap customizations-table-wrap--etl">
                    <table className="customizations-table customizations-table--etl">
                        <thead>
                            <tr>
                                <th>Key</th>
                                <th>Description</th>
                                <th>Type</th>
                                <th>Default</th>
                                <th>Override</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {isEtlContentLoading && (
                                <tr>
                                    <td colSpan={etlTableColSpan}>
                                        <div className="customizations-empty-state customizations-empty-state--loading customizations-empty-state--table-loading" aria-busy="true" aria-label="Loading ETL customizations">
                                            <Spinner isSmall />
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {!isEtlContentLoading && rows.length ? rows.map((row: any) => {
                                const canEdit = !!selectedSystem && !!selectedEntity
                                const actionMessage = !canEdit
                                    ? etlEditDisabledMessage
                                    : row.has_override
                                        ? 'Edit override'
                                        : 'Edit default'
                                return (
                                    <tr key={`${bucket}:${row.key}`} className={row.has_override ? 'is-overridden' : ''}>
                                        <td>{renderKeyDescriptionCell(row.key, row.description)}</td>
                                        <td className="customizations-table__hidden-cell" aria-hidden="true" />
                                        <td><span className="customizations-pill">{row.type}</span></td>
                                        <td>
                                            <div className={`customizations-value-cell customizations-value-cell--default${row.has_override ? ' is-muted' : ''}`}>
                                                <span className="customizations-value-cell__label">Default</span>
                                                <span className={`customizations-value-cell__value${row.has_override ? ' is-dimmed' : ''}`}>
                                                    {isListType(row.type) ? renderListChips(row.default) : formatValue(row.default)}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            {row.has_override
                                                ? (
                                                    <div className="customizations-value-cell customizations-value-cell--override">
                                                        <span className="customizations-value-cell__value">
                                                            {isListType(row.type) ? renderListChips(row.override) : formatValue(row.override)}
                                                        </span>
                                                    </div>
                                                )
                                                : (
                                                    <div className="customizations-value-cell customizations-value-cell--empty">
                                                        <span className="customizations-value-cell__value">
                                                            <span className="customizations-comparison-cell__empty" aria-hidden="true">-</span>
                                                        </span>
                                                    </div>
                                                )}
                                        </td>
                                        <td>
                                            <span className="customizations-row-actions">
                                                <span className="customizations-edit-wrapper">
                                                    <button
                                                        type="button"
                                                        className="customizations-edit-btn"
                                                        onClick={() => openEtlEditor(bucket, row)}
                                                        disabled={!canEdit}
                                                        title={!canEdit ? actionMessage || undefined : undefined}
                                                        aria-label={actionMessage || undefined}
                                                    >
                                                        <IconPencil />
                                                    </button>
                                                    {!canEdit && (
                                                        <span className="customizations-edit-tooltip" role="tooltip">
                                                            {actionMessage}
                                                        </span>
                                                    )}
                                                </span>
                                            </span>
                                        </td>
                                    </tr>
                                )
                            }) : !isEtlContentLoading ? (
                                <tr>
                                    <td colSpan={etlTableColSpan}>
                                        <div className="customizations-empty-state">
                                            No rows found for this bucket.
                                        </div>
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </section>
        )
    }

    const renderRuleRows = (objective: string, rows: any[], mode: 'flow' | 'parameters') => {
        const hasRows = mode === 'flow'
            ? rows.length > 0
            : buildParameterTableRows(rows, objective, getRuleRowScopeEntries, getRuleScopeLabel).length > 0
        if (!hasRows && !isRuleContentLoading) return null

        const parameterRows = mode === 'parameters'
            ? buildParameterTableRows(rows, objective, getRuleRowScopeEntries, getRuleScopeLabel)
            : []

        return (
            <div className={`customizations-rule-section${mode === 'parameters' ? ' customizations-rule-section--parameters' : ''}`}>
                <div className="customizations-rule-section__header">
                    <div>
                        <h3 className="customizations-rule-section__title">
                            {mode === 'flow' ? 'Flow Suppressions' : 'Parameters'}
                        </h3>
                        <p className="customizations-rule-section__description">
                            {mode === 'flow'
                                ? 'a suppressed flow is skipped entirely for this entity on the selected scope'
                                : 'platform default applies unless an override is set; values are typed'}
                        </p>
                    </div>
                </div>

                <div className={`customizations-table-wrap customizations-table-wrap--rule${mode === 'flow' ? ' customizations-table-wrap--flow' : ''}`}>
                    <table className="customizations-table customizations-table--rule">
                        <thead>
                            <tr>
                                {mode === 'flow' ? (
                                    <>
                                        <th>Flow</th>
                                        <th>Status</th>
                                        <th>Scope</th>
                                        <th />
                                    </>
                                ) : (
                                    <>
                                        <th>Rule · Param</th>
                                        <th>Description</th>
                                        <th>Type</th>
                                        <th>Default</th>
                                        <th>Override</th>
                                        <th>Scope</th>
                                        <th />
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {isRuleContentLoading && (
                                <tr>
                                    <td colSpan={mode === 'flow' ? 4 : 7}>
                                        <div className="customizations-empty-state customizations-empty-state--loading customizations-empty-state--table-loading" aria-busy="true" aria-label={`Loading ${mode === 'flow' ? 'flow suppressions' : 'parameters'}`}>
                                            <Spinner isSmall />
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {!isRuleContentLoading && mode === 'flow' && rows.length ? rows.map((row: any) => {
                                const { scope, scopes, override } = getRuleRowOverride(objective, row)
                                const isActive = override?.active ?? row.active_default ?? true
                                const hasOverride = Object.keys(row.override_scopes || {}).length > 0
                                const scopeLabel = scopes.includes(ALL_SYSTEMS_SCOPE)
                                    ? 'All systems (*)'
                                    : scopes.length > 0
                                        ? scopes.join(', ')
                                        : (scope === '*' ? 'All systems (*)' : scope)
                                const stateLabel = isActive ? 'ACTIVE' : 'SUPPRESSED'
                                return (
                                    <tr key={`${objective}:${row.rule}`} className={hasOverride ? 'is-overridden' : ''}>
                                        <td><FlowDescriptionCell label={row.rule} description={row.description} /></td>
                                        <td>
                                            <span className={`customizations-state-pill ${isActive ? 'is-active' : 'is-suppressed'}`}>
                                                {stateLabel}
                                            </span>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="customizations-scope-badge customizations-scope-badge--button"
                                                title={scopeLabel}
                                                onClick={() => openRuleEditor(objective, row, scope)}
                                                aria-label={`Edit scope for ${row.rule}. Current scope ${scopeLabel}`}
                                                disabled={!selectedEntity || isS3DefaultRow(row)}
                                            >
                                                <span className="customizations-scope-badge__label">on:</span>
                                                <span className="customizations-scope-badge__value">{scopeLabel}</span>
                                            </button>
                                        </td>
                                        <td>
                                            <span className="customizations-row-actions">
                                                <span className="customizations-edit-wrapper">
                                                    <button
                                                        type="button"
                                                        className={`customizations-toggle-btn${isActive ? ' is-on' : ' is-off'}`}
                                                        onClick={() => toggleFlowSuppressionState(objective, row)}
                                                        disabled={!selectedEntity || isS3DefaultRow(row)}
                                                        aria-label={isActive ? `On ${row.rule}` : `Off ${row.rule}`}
                                                    >
                                                        <span className="customizations-toggle-btn__track">
                                                            <span className="customizations-toggle-btn__thumb" />
                                                        </span>
                                                    </button>
                                                    {getS3EditTooltip(!selectedEntity || isS3DefaultRow(row), ruleEditDisabledMessage || '') && (
                                                        <span className="customizations-edit-tooltip" role="tooltip">
                                                            {getS3EditTooltip(!selectedEntity || isS3DefaultRow(row), ruleEditDisabledMessage || '')}
                                                        </span>
                                                    )}
                                                </span>
                                            </span>
                                        </td>
                                    </tr>
                                )
                            }) : !isRuleContentLoading && mode === 'parameters' && parameterRows.length ? parameterRows.map((paramRow: any) => {
                                const { row, scope, hasOverride, scopeLabel, paramName, description, type, defaultValue, overrideValue } = paramRow
                                return (
                                    <tr key={paramRow.key} className={hasOverride ? 'is-overridden' : ''}>
                                        <td><strong>{row.rule}</strong><span className="customizations-rule-param-name">.{paramName}</span></td>
                                        <td>{description}</td>
                                        <td><span className="customizations-pill">{type}</span></td>
                                        <td>{formatValue(defaultValue)}</td>
                                        <td>
                                            <div className={`customizations-value-cell${hasOverride ? ' customizations-value-cell--override' : ''}`}>
                                                <span className="customizations-value-cell__value">
                                                {hasOverride ? formatValue(overrideValue) : <span className="customizations-comparison-cell__empty" aria-hidden="true">-</span>}
                                            </span>
                                        </div>
                                    </td>
                                        <td>
                                            <span className="customizations-scope-badge" title={scopeLabel}>
                                                <span className="customizations-scope-badge__label">on:</span>
                                                <span className="customizations-scope-badge__value">{scopeLabel}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <span className="customizations-row-actions">
                                                <span className="customizations-edit-wrapper">
                                                    <button
                                                        type="button"
                                                        className="customizations-edit-btn"
                                                        onClick={() => openRuleEditor(objective, row, scope, paramRow.param)}
                                                        disabled={!selectedEntity || isS3DefaultRow(row)}
                                                        aria-label={getEditDisabledMessage(!selectedEntity || isS3DefaultRow(row), ruleEditDisabledMessage || '', 'This row is using the S3 default. Create a customization before editing.')}
                                                    >
                                                        <IconPencil />
                                                    </button>
                                                    {getS3EditTooltip(!selectedEntity || isS3DefaultRow(row), ruleEditDisabledMessage || '') && (
                                                        <span className="customizations-edit-tooltip" role="tooltip">
                                                            {getS3EditTooltip(!selectedEntity || isS3DefaultRow(row), ruleEditDisabledMessage || '')}
                                                        </span>
                                                    )}
                                                </span>
                                            </span>
                                        </td>
                                    </tr>
                                )
                            }) : !isRuleContentLoading ? (
                                <tr>
                                    <td colSpan={mode === 'flow' ? 4 : 7}>
                                        <div className="customizations-empty-state">
                                            Choose an entity to view {mode === 'flow' ? 'flow suppressions' : 'parameters'}.
                                        </div>
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
        )
    }

    const renderRuleObjectivePanel = (objective: string, rows: any[]) => {
        const flowRows = rows.filter(isFlowSuppressionRule)
        const parameterRows = rows.filter(isParameterRule)

        return (
            <section key={objective} className="customizations-panel customizations-panel--compare">
                <div className="customizations-panel__header">
                    <div>
                        <div className="customizations-panel__title-row">
                            <h2>{objective}</h2>
                            <span className={`customizations-source-pill ${ruleSourceTone}`}>{ruleSourceLabel}</span>
                        </div>
                        <p className="customizations-panel__subtitle">{rows.length} rules</p>
                    </div>
                </div>

                {renderRuleRows(objective, flowRows, 'flow')}
                {renderRuleRows(objective, parameterRows, 'parameters')}
            </section>
        )
    }

    const buildEtlSnapshot = useCallback((pendingEditor?: EditorState | null) => {
        const snapshot: Record<string, Record<string, any>> = {}

        etlObjectives.forEach(([bucket, rows]) => {
            snapshot[bucket] = {}
            rows.forEach((row: any) => {
                snapshot[bucket][row.key] = row.effective
            })
        })

        if (!pendingEditor || pendingEditor.kind !== 'etl' || !pendingEditor.bucket) return snapshot

        const { bucket, row } = pendingEditor
        const pendingListItems = isListType(row.type)
            ? [
                ...(pendingEditor.listItems || []),
                ...(pendingEditor.listInput?.trim() ? [pendingEditor.listInput.trim()] : []),
            ]
            : []
        const currentValue = row.type === 'bool'
            ? pendingEditor.value === 'true'
            : row.type === 'int'
                ? Number(pendingEditor.value)
                : isListType(row.type)
                    ? pendingListItems
                    : ((row.type || '').startsWith('dict') || row.type === 'json')
                        ? parseJson(pendingEditor.value || '')
                        : pendingEditor.value ?? ''

        snapshot[bucket] = snapshot[bucket] || {}
        snapshot[bucket][row.key] = currentValue
        return snapshot
    }, [etlObjectives])

    const openEtlEditor = useCallback((bucket: string, row: any) => {
        const overrideValue = row.has_override ? row.override : row.default
        setEditorError('')
        setEditor({
            kind: 'etl',
            mode: 'edit',
            bucket,
            row,
            value: row.type === 'bool' ? String(Boolean(overrideValue)) : typeof overrideValue === 'object' ? prettyJson(overrideValue) : String(overrideValue ?? ''),
            listItems: isListType(row.type) ? normalizeListItems(overrideValue) : undefined,
            listInput: '',
        })
    }, [])

    const openDeleteEditor = useCallback((
        kind: EditorKind,
        row: any,
        params: Partial<Pick<EditorState, 'bucket' | 'objective' | 'rule' | 'scope' | 'deleteSummary' | 'deleteDescription'>> = {}
    ) => {
        setEditorError('')
        setEditor({
            kind,
            mode: 'delete',
            row,
            bucket: params.bucket,
            objective: params.objective,
            rule: params.rule,
            scope: params.scope,
            deleteReason: '',
            deleteSummary: params.deleteSummary,
            deleteDescription: params.deleteDescription,
        })
    }, [])

    const openRuleEditor = useCallback((objective: string, row: any, scopeOverride?: string, paramOverride?: any) => {
        const scopes = Object.keys(row.override_scopes || {})
        const scope = scopeOverride || (scopes.includes(ALL_SYSTEMS_SCOPE) ? ALL_SYSTEMS_SCOPE : scopes[0] || '')
        const current = ruleOverrideRoot?.[objective]?.[scope]?.[row.rule]
            || row.override_scopes?.[scope]?.[row.rule]
            || {}
        const selectedParam = paramOverride || getRuleParamDefinition(row)
        setEditorError('')
        setEditor({
            kind: 'rule',
            mode: 'edit',
            objective,
            rule: row.rule,
            row,
            param: selectedParam,
            scope,
            scopes: resolveRuleEditorScopes(row, scopeOverride),
            active: current.active ?? row.active_default ?? true,
            params: selectedParam
                ? resolveRuleParamEditorValue(current, { ...row, param: selectedParam })
                : resolveRuleParamsEditorValue(current, row),
        })
    }, [ruleOverrideRoot])

    const persistRuleManagerOverrides = useCallback(async (nextOverrides: Record<string, any>) => {
        await newHttpService.put('/api/backoffice/customizations/rule-manager', {
            company_name: companyName,
            ...compactParams({ entity_id: selectedEntity?.id }),
            overrides: nextOverrides,
        })
        refetchEntities()
        await refetchRule()
    }, [companyName, refetchEntities, refetchRule, selectedEntity?.id])

    const toggleFlowSuppressionState = useCallback(async (objective: string, row: any) => {
        if (!selectedEntity || isS3DefaultRow(row)) return
        const currentScope = Object.keys(row?.override_scopes || {})[0] || ALL_SYSTEMS_SCOPE
        const currentOverride = getRuleOverrideForScope(objective, row, currentScope, ruleOverrideRoot)
        const currentActive = currentOverride?.active ?? row.active_default ?? true
        const nextActive = !currentActive
        const nextStateLabel = nextActive ? 'active' : 'suppressed'
        const confirmed = window.confirm(`Are you sure you want to set ${row.rule} to ${nextStateLabel}?`)
        if (!confirmed) return

        const nextOverrides = buildFlowSuppressionTogglePayload(ruleOverrideRoot, objective, row, nextActive)
        await persistRuleManagerOverrides(nextOverrides)
        dispatch(setUserMsg({ msg: { type: 'success', txt: 'Customization saved' } }))
    }, [dispatch, persistRuleManagerOverrides, ruleOverrideRoot, selectedEntity])

    const closeEditor = useCallback(() => setEditor(null), [])

    const saveEditor = useCallback(async () => {
        if (!editor) return

        setIsSubmitting(true)
        setEditorError('')
        try {
                if (editor.mode === 'delete') {
                    if (!editor.deleteReason?.trim()) {
                        throw new Error('reason is required')
                    }
                    if (editor.kind === 'etl') {
                        await newHttpService.delete('/api/backoffice/customizations/etl', {
                            company_name: companyName,
                            ...compactParams(etlSelectionParams),
                            reason: editor.deleteReason.trim(),
                        })
                        if (selectedEntitySystem?.id) {
                            setEtlCustomizationOverrides(prev => ({
                                ...prev,
                                [String(selectedEntitySystem.id)]: false,
                            }))
                        }
                        refetchEntitySystems()
                        await refetchEtlOverride()
                    } else {
                        await newHttpService.delete('/api/backoffice/customizations/rule-manager', {
                            company_name: companyName,
                            ...compactParams({ entity_id: selectedEntity?.id, entity_name: selectedEntity?.name }),
                            reason: editor.deleteReason.trim(),
                        })
                        refetchEntities()
                        await refetchRule()
                    }
                } else if (editor.kind === 'etl') {
                    if (isListType(editor.row.type)) {
                        const pendingItems = [
                            ...(editor.listItems || []),
                            ...(editor.listInput?.trim() ? [editor.listInput.trim()] : []),
                        ]
                        const invalidItem = pendingItems.find(item => !isPlainListItem(item))
                        if (invalidItem) {
                            throw new Error(`Invalid list item "${invalidItem}". Enter plain strings only, without quotes or brackets.`)
                        }
                    }
                    const nextOverrides = buildEtlSnapshot(editor)
                    await newHttpService.put('/api/backoffice/customizations/etl', {
                        company_name: companyName,
                        ...compactParams(etlSelectionParams),
                        overrides: nextOverrides,
                    })
                    if (selectedEntitySystem?.id) {
                        setEtlCustomizationOverrides(prev => ({
                            ...prev,
                            [String(selectedEntitySystem.id)]: true,
                        }))
                    }
                    refetchEntitySystems()
                    await refetchEtlOverride()
                } else {
                    const selectedScopes = normalizeRuleScopeSelection(editor.scopes || (editor.scope ? [editor.scope] : []))
                    if (!selectedScopes.length) {
                        throw new Error('Scope is required')
                    }
                    if (selectedScopes.includes(ALL_SYSTEMS_SCOPE) && selectedScopes.length > 1) {
                        throw new Error('All systems must be selected alone')
                    }
                    const invalidScope = selectedScopes.find(scope => scope !== ALL_SYSTEMS_SCOPE && (!isNormalizedSystemKey(scope) || !ruleScopeOptionValues.has(scope)))
                    if (invalidScope) {
                        const allowedScopes = ruleScopeOptions.map(option => option.value).join(', ')
                        throw new Error(allowedScopes
                            ? `Scope must match one of this entity's systems: ${allowedScopes}`
                            : 'This entity has no systems to choose from')
                    }
                    const objective = editor.objective as string
                    const isFlowSuppression = isFlowSuppressionRule(editor.row)
                    let nextOverrides: Record<string, any> = cloneJson(ruleOverrideRoot)
                    if (!isFlowSuppression) {
                        const paramDefinition = editor.param || getRuleParamDefinition(editor.row)
                        if (paramDefinition?.name) {
                            const scopeEntries = editor.scopeEntries || []
                            const ruleScopes = normalizeRuleScopeSelection([ALL_SYSTEMS_SCOPE, ...scopeEntries.map(entry => entry.scope)])
                            const invalidScope = ruleScopes.find(scope => scope !== ALL_SYSTEMS_SCOPE && (!isNormalizedSystemKey(scope) || !ruleScopeOptionValues.has(scope)))
                            if (invalidScope) {
                                const allowedScopes = ruleScopeOptions.map(option => option.value).join(', ')
                                throw new Error(allowedScopes
                                    ? `Scope must match one of this entity's systems: ${allowedScopes}`
                                    : 'This entity has no systems to choose from')
                            }

                            const desiredScopes = new Set(ruleScopes)
                            const objectiveOverrides = nextOverrides[objective] && typeof nextOverrides[objective] === 'object'
                                ? nextOverrides[objective]
                                : {}
                            Object.keys(objectiveOverrides).forEach(scope => {
                                if (scope === ALL_SYSTEMS_SCOPE || desiredScopes.has(scope)) return
                                const scopeRules = objectiveOverrides[scope]
                                if (scopeRules && scopeRules[editor.rule as string]) {
                                    delete scopeRules[editor.rule as string]
                                    if (!Object.keys(scopeRules).length) {
                                        delete objectiveOverrides[scope]
                                    }
                                }
                            })

                            const entriesToSave = [
                                { scope: ALL_SYSTEMS_SCOPE, value: editor.params || '' },
                                ...scopeEntries,
                            ]

                            entriesToSave.forEach(entry => {
                                const scope = entry.scope || ALL_SYSTEMS_SCOPE
                                const parsedValue = parseRuleParamEditorValue({ ...editor.row, param: paramDefinition }, entry.value || '')
                                validateRuleParamValue(paramDefinition.name, parsedValue, paramDefinition.type)
                                const scopeOverride = getRuleOverrideForScope(objective, editor.row, scope, ruleOverrideRoot)
                                const nextParams = mergeRuleParamsForSave(editor.row, scopeOverride?.params, {
                                    [paramDefinition.name]: parsedValue,
                                })
                                objectiveOverrides[scope] = {
                                    ...(objectiveOverrides[scope] || {}),
                                    [editor.rule as string]: {
                                        active: scopeOverride?.active ?? editor.row.active_default ?? true,
                                        params: nextParams,
                                    },
                                }
                            })

                            nextOverrides[objective] = objectiveOverrides
                        } else {
                            const rawParams = parseJson(editor.params || '{}')
                            validateRuleParamsForSave(editor.row, rawParams)
                            const currentScope = editor.scope || ALL_SYSTEMS_SCOPE
                            const currentOverride = getRuleOverrideForScope(objective, editor.row, currentScope, ruleOverrideRoot)
                            const nextParams = mergeRuleParamsForSave(editor.row, currentOverride?.params, rawParams)
                            nextOverrides = mergeRuleOverridePayload(
                                nextOverrides,
                                objective,
                                editor.rule as string,
                                [currentScope],
                                currentOverride?.active ?? editor.row.active_default ?? true,
                                nextParams,
                            )
                        }
                    } else {
                        const selectedScopes = normalizeRuleScopeSelection(editor.scopes || (editor.scope ? [editor.scope] : []))
                        if (!selectedScopes.length) {
                            throw new Error('Scope is required')
                        }
                        if (selectedScopes.includes(ALL_SYSTEMS_SCOPE) && selectedScopes.length > 1) {
                            throw new Error('All systems must be selected alone')
                        }
                        const invalidScope = selectedScopes.find(scope => scope !== ALL_SYSTEMS_SCOPE && (!isNormalizedSystemKey(scope) || !ruleScopeOptionValues.has(scope)))
                        if (invalidScope) {
                            const allowedScopes = ruleScopeOptions.map(option => option.value).join(', ')
                            throw new Error(allowedScopes
                                ? `Scope must match one of this entity's systems: ${allowedScopes}`
                                : 'This entity has no systems to choose from')
                        }
                        const currentScope = selectedScopes[0] || editor.scope || ALL_SYSTEMS_SCOPE
                        const currentOverride = getRuleOverrideForScope(objective, editor.row, currentScope, ruleOverrideRoot)
                        nextOverrides = mergeExclusiveRuleOverridePayload(
                            {
                                ...nextOverrides,
                            },
                            objective,
                            editor.rule as string,
                            selectedScopes,
                            !!editor.active,
                            currentOverride?.params || {},
                        )
                    }
                    await persistRuleManagerOverrides(nextOverrides)
                }
                closeEditor()
                dispatch(setUserMsg({ msg: { type: 'success', txt: 'Customization saved' } }))
        } catch (error) {
            const errorMessage = await readErrorMessage(error)
            const message = errorMessage
                ? `Failed to save customization: ${errorMessage}`
                : 'Failed to save customization'
            setEditorError(message)
            dispatch(setUserMsg({ msg: { type: 'danger', txt: message } }))
        } finally {
            setIsSubmitting(false)
        }
    }, [
        closeEditor,
        dispatch,
        editor,
        buildEtlSnapshot,
        companyName,
        refetchEntities,
        persistRuleManagerOverrides,
        refetchEtlOverride,
        refetchEntitySystems,
        refetchRule,
        etlSelectionParams,
        ruleOverrideRoot,
        ruleScopeOptionValues,
        ruleScopeOptions,
        selectedEntity?.id,
        selectedEntity?.name,
        selectedEntitySystem?.id,
    ])

    const addEditorListItem = useCallback(() => {
        if (!editor || !isListType(editor.row.type)) return
        const nextItem = (editor.listInput || '').trim()
        if (!nextItem) return
        setEditor(prev => prev ? {
            ...prev,
            listItems: [...(prev.listItems || []), nextItem],
            listInput: '',
        } : prev)
    }, [editor])

    const removeEditorListItem = useCallback((itemToRemove: string) => {
        setEditor(prev => prev && isListType(prev.row.type) ? {
            ...prev,
            listItems: (prev.listItems || []).filter(item => item !== itemToRemove),
        } : prev)
    }, [])

    const editorRuleParam = editor?.kind === 'rule' ? (editor.param || getRuleParamDefinition(editor.row)) : null
    const editorRuleScopeEntries = editor?.kind === 'rule' && !isFlowSuppressionRule(editor.row)
        ? (editor.scopeEntries || [])
        : []

    const addEditorRuleScopeEntry = useCallback(() => {
        if (!editor || editor.kind !== 'rule' || isFlowSuppressionRule(editor.row) || !editorRuleParam) return
        setEditor(prev => {
            if (!prev || prev.kind !== 'rule') return prev
            const usedScopes = new Set((prev.scopeEntries || []).map(entry => entry.scope).filter(Boolean))
            const nextScope = ruleScopeOptions.find(option => option.value !== ALL_SYSTEMS_SCOPE && !usedScopes.has(option.value))?.value || ''
            if (!nextScope) return prev
            return {
                ...prev,
                scopeEntries: [
                    ...(prev.scopeEntries || []),
                    {
                        scope: nextScope,
                        value: '',
                    },
                ],
            }
        })
    }, [editor, editorRuleParam, ruleScopeOptions])

    const updateEditorRuleScopeEntry = useCallback((index: number, patch: Partial<{ scope: string; value: string }>) => {
        setEditor(prev => {
            if (!prev || prev.kind !== 'rule') return prev
            const nextEntries = [...(prev.scopeEntries || [])]
            if (!nextEntries[index]) return prev
            nextEntries[index] = {
                ...nextEntries[index],
                ...patch,
            }
            return {
                ...prev,
                scopeEntries: nextEntries,
            }
        })
    }, [])

    const removeEditorRuleScopeEntry = useCallback((index: number) => {
        setEditor(prev => {
            if (!prev || prev.kind !== 'rule') return prev
            const nextEntries = [...(prev.scopeEntries || [])]
            nextEntries.splice(index, 1)
            return {
                ...prev,
                scopeEntries: nextEntries,
            }
        })
    }, [])

    if (!companyName) {
        return <div className="tablePageContainer"><SelectCompanyPrompt /></div>
    }

    const isEtlContentLoading = isEtlPage && Boolean(selectedSystemKey) && (isEtlDefaultsLoading || isEtlOverrideLoading)
    const isRuleContentLoading = !isEtlPage && isRuleLoading
    const hasError = hasEntitiesError || (isEtlPage ? (hasEtlDefaultsError || hasEtlOverrideError) : hasRuleError)
    const entitySelectValue = selectedEntityId !== null ? String(selectedEntityId) : ''
    const systemSelectValue = selectedSystemKey || ''
    const etlEditDisabledMessage = selectedSystem
        ? selectedEntity
            ? null
            : 'Select an entity to edit defaults'
        : 'Select a system first'
    const ruleEditDisabledMessage = selectedEntity
        ? null
        : 'Select an entity to edit'
    const etlBannerMessage = !selectedSystem
        ? 'Select a system to view defaults, then an entity to customize them.'
        : !selectedEntitySystem
            ? 'Showing the selected system defaults. Select an entity to customize them.'
            : null
    const primarySelectedBucket = selectedBuckets[0] || etlObjectives[0]?.[0] || ''
    const etlDeleteCount = selectedEtlObjectives.reduce((sum, [, rows]) => (
        sum + (rows as any[]).filter((row: any) => !!row.has_override).length
    ), 0)
    const ruleDeleteCount = selectedRuleObjectives.reduce((sum, [, rows]) => (
        sum + (rows as any[]).filter((row: any) => Object.keys(row.override_scopes || {}).length > 0).length
    ), 0)
    const etlDeleteLabel = getDeleteCustomizationLabel(etlDeleteCount)
    const ruleDeleteLabel = getDeleteCustomizationLabel(ruleDeleteCount)
    const editorModeLabel = editor
        ? editor.mode === 'delete'
            ? (editor.kind === 'etl' ? etlDeleteLabel : ruleDeleteLabel)
            : editor.kind === 'etl'
            ? (editor.row.has_override ? 'Edit override' : 'Edit default')
            : isFlowSuppressionRule(editor.row)
                ? 'Edit flow suppression'
                : 'Edit override'
        : 'Edit override'
    const entityDropdownPlaceholder = isEtlPage
        ? (selectedSystem ? 'Select entity' : 'Select system first')
        : 'Select entity'
    const entityDropdownDisabled = isEtlPage ? !selectedSystem : false
    const entityDropdownLoading = isEtlPage
        ? (isEntitiesLoading || isEntitySystemsLoading)
        : isEntitiesLoading
    const etlTableColSpan = 6
    const sidebar = isEtlPage ? (
        <aside className="customizations-sidebar">
            {hasSelectedEtlOverride && selectedSystem && selectedEntity && (
                <button
                    type="button"
                    className="customizations-panel__remove-btn customizations-sidebar__remove-btn"
                    onClick={() => openDeleteEditor('etl', selectedEntitySystem || selectedEntity, {
                        bucket: primarySelectedBucket,
                        deleteSummary: etlDeleteLabel,
                        deleteDescription: `Delete the customization${etlDeleteCount > 1 ? 's' : ''} for ${selectedSystem.label || selectedSystemKey} / ${selectedEntity.name}. The shipped defaults will apply again.`,
                    })}
                >
                    {etlDeleteLabel}
                </button>
            )}
            <p className="customizations-sidebar__title">Objectives</p>
            {etlObjectives.map(([bucket, rows]) => {
                const isSelected = selectedBuckets.includes(bucket as string)
                const overrideCount = (rows as any[]).filter((row: any) => !!row.has_override).length
                const totalCount = (rows as any[]).length
                return (
                    <button
                        key={bucket}
                        className={`customizations-sidebar__item${isSelected ? ' active' : ''}`}
                        onClick={() => toggleEtlBucket(bucket as string)}
                    >
                        <span className="customizations-sidebar__itemMain">
                            <span className="customizations-sidebar__itemLabel">{bucketLabel(bucket as string)}</span>
                        </span>
                        <span className="customizations-sidebar__count" aria-label={`${overrideCount} overridden out of ${totalCount} rows`}>
                            <strong>{overrideCount}</strong>
                            <span>/{totalCount}</span>
                        </span>
                    </button>
                )
            })}
        </aside>
    ) : (
        <aside className="customizations-sidebar">
            {ruleData?.customization_source === 'database' && selectedEntity && (
                <button
                    type="button"
                    className="customizations-panel__remove-btn customizations-sidebar__remove-btn"
                    onClick={() => openDeleteEditor('rule', selectedEntity, {
                        deleteSummary: ruleDeleteLabel,
                        deleteDescription: `Delete the customization${ruleDeleteCount > 1 ? 's' : ''} for ${selectedEntity.name}. The catalog defaults will apply again.`,
                    })}
                >
                    {ruleDeleteLabel}
                </button>
            )}
            <p className="customizations-sidebar__title">Objectives</p>
            {isRuleContentLoading ? (
                <div className="customizations-loading-inline customizations-loading-inline--rule" aria-busy="true" aria-label="Loading rule manager customizations">
                    <Spinner isSmall />
                </div>
            ) : (
                ruleObjectives.map(([objective, rows]) => {
                    const isSelected = selectedObjectives.includes(objective)
                    const overrideCount = (rows as any[]).filter((row: any) => Object.keys(row.override_scopes || {}).length > 0).length
                    const totalCount = (rows as any[]).length
                    return (
                        <button
                            key={objective}
                            className={`customizations-sidebar__item${isSelected ? ' active' : ''}`}
                            onClick={() => toggleRuleObjective(objective as string)}
                        >
                            <span className="customizations-sidebar__itemMain">
                                <span className="customizations-sidebar__itemLabel">{objective}</span>
                            </span>
                            <span className="customizations-sidebar__count" aria-label={`${overrideCount} overridden out of ${totalCount} rules`}>
                                <strong>{overrideCount}</strong>
                                <span>/{totalCount}</span>
                            </span>
                        </button>
                    )
                })
            )}
        </aside>
    )

    return (
        <div className="tablePageContainer customizations-page">
            <div className="page-header customizations-page__header">
                <div>
                    <p className="tableSubtitle">Manage shipped defaults and customizations.</p>
                </div>
                <div className="page-header-actions customizations-page__actions">
                    <div className="customizations-page__selectors">
                        {isEtlPage && (
                            <CustomizationsDropdown
                                label="System"
                                value={systemSelectValue}
                                placeholder="Select system"
                                options={systemOptions}
                                isLoading={isEtlMetaLoading && availableSystems.length === 0}
                                dropdownRefreshScope="customizations-page"
                                onChange={nextValue => {
                                    const nextKey = nextValue || null
                                    syncSelectionToSearchParams(nextKey, null)
                                }}
                            />
                        )}
                        <CustomizationsDropdown
                            label="Entity"
                            value={entitySelectValue}
                            placeholder={entityDropdownPlaceholder}
                            options={entityOptions}
                            disabled={entityDropdownDisabled}
                            isLoading={entityDropdownLoading}
                            dropdownRefreshScope="customizations-page"
                            onChange={nextValue => {
                                const nextId = nextValue ? Number(nextValue) : null
                                if (isEtlPage) {
                                syncSelectionToSearchParams(selectedSystemKey, nextId)
                            } else {
                                syncSelectionToSearchParams(null, nextId)
                                }
                            }}
                        />
                    </div>
                </div>
            </div>

            {hasError && <div className="error-hint">Failed to load customizations. Try refreshing the page.</div>}

            {!hasError && (
                <div className="customizations-page__body">
                    {sidebar}
                    <div className="customizations-page__content">
                        {isEtlPage ? (
                            <div className="customizations-compare">
                                {etlBannerMessage && (
                                    <div className="customizations-default-banner">
                                        {etlBannerMessage}
                                    </div>
                                )}

                                {selectedEtlObjectives.map(([bucket, rows]) => renderEtlBucketPanel(bucket as string, rows as any[]))}
                            </div>
                        ) : (
                            <div className="customizations-compare">
                                {ruleData?.customization_source === 's3' && selectedRuleObjectives.length > 0 && (
                                    <div className="customizations-default-banner">
                                        No customization exists for this entity yet. Showing the catalog defaults.
                                    </div>
                                )}

                                {selectedRuleObjectives.map(([objective, rows]) => (
                                    renderRuleObjectivePanel(objective as string, rows as any[])
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
            {editor && (
                <SlideDrawer isOpen={!!editor} onClose={closeEditor} variant="modal">
                    <div className="customizations-editor">
                        <p className="customizations-editor__eyebrow">{editorModeLabel}</p>
                        <h2>
                            {editor.mode === 'delete'
                                ? (editor.deleteSummary || 'Remove override')
                                : editor.kind === 'etl'
                                    ? `${editor.bucket} / ${editor.row.key}`
                                    : `${editor.objective} / ${editor.rule}`}
                        </h2>
                        <p className="customizations-editor__text">
                            {editor.mode === 'delete'
                                ? (editor.deleteDescription || 'This will delete the current customization and keep the shipped default in place.')
                                : editor.row.description}
                        </p>

                            {editor.mode === 'delete' ? (
                            <>
                                <div className="customizations-editor__meta">
                                    <span className="customizations-pill">{editor.kind === 'etl' ? etlDeleteLabel : ruleDeleteLabel}</span>
                                    <span>
                                        {editor.kind === 'etl'
                                            ? `${editor.bucket} / ${editor.row.key}`
                                            : editor.row?.name || editor.row?.entity?.name || editor.row?.label || 'Selected entity'}
                                    </span>
                                </div>

                                <label className="customizations-field">
                                    <span>Reason for deletion</span>
                                    <textarea
                                        value={editor.deleteReason || ''}
                                        onChange={e => setEditor(prev => prev ? { ...prev, deleteReason: e.target.value } : prev)}
                                        rows={3}
                                        placeholder="Explain why this customization is being removed"
                                    />
                                </label>
                            </>
                        ) : editor.kind === 'etl' ? (
                            <>
                                <div className="customizations-editor__meta">
                                    <span className="customizations-pill">{editor.row.type}</span>
                                    <span>Default: {formatValue(editor.row.default)}</span>
                                    <span>Effective: {formatValue(editor.row.effective)}</span>
                                </div>

                                <label className="customizations-field">
                                    <span>Override value</span>
                                    {editor.row.type === 'bool' ? (
                                        <select value={editor.value} onChange={e => setEditor(prev => prev ? { ...prev, value: e.target.value } : prev)}>
                                            <option value="true">true</option>
                                            <option value="false">false</option>
                                        </select>
                                    ) : editor.row.type === 'int' ? (
                                        <input type="number" value={editor.value} onChange={e => setEditor(prev => prev ? { ...prev, value: e.target.value } : prev)} />
                                    ) : isListType(editor.row.type) ? (
                                        <div className="customizations-chip-editor">
                                            <div className="customizations-chip-row customizations-chip-row--editor">
                                                {(editor.listItems || []).map(item => (
                                                    <button
                                                        key={item}
                                                        type="button"
                                                        className="customizations-chip customizations-chip--removable"
                                                        onClick={() => removeEditorListItem(item)}
                                                        title={`Remove ${item}`}
                                                    >
                                                        <span>{item}</span>
                                                        <span aria-hidden="true">×</span>
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="customizations-chip-input-row">
                                                <input
                                                    type="text"
                                                    value={editor.listInput || ''}
                                                    onChange={e => setEditor(prev => prev ? { ...prev, listInput: e.target.value } : prev)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault()
                                                            addEditorListItem()
                                                        }
                                                    }}
                                                    placeholder="Add item"
                                                />
                                                <button type="button" className="secondary small" onClick={addEditorListItem}>Add</button>
                                            </div>
                                            <small className="customizations-field__hint">
                                                Add plain strings only, like <code>admin</code> or <code>admin2</code>. Do not paste JSON or quote the value.
                                            </small>
                                        </div>
                                    ) : (
                                        <textarea value={editor.value} onChange={e => setEditor(prev => prev ? { ...prev, value: e.target.value } : prev)} rows={10} />
                                    )}
                                    {(editor.row.type?.startsWith('dict') || editor.row.type === 'json') && (
                                        <small className="customizations-field__hint">
                                            Enter valid JSON. For a dict of lists, use something like <code>{'{"groups":["a","b"]}'}</code>.
                                        </small>
                                    )}
                                </label>
                            </>
                        ) : (
                            <>
                                <div className="customizations-editor__meta">
                                    <span className="customizations-pill">
                                        {isFlowSuppressionRule(editor.row) ? 'Flow suppression' : 'Rule'}
                                    </span>
                                    {isFlowSuppressionRule(editor.row) && (
                                        <span>
                                            Default status: {editor.row.active_default ? 'active' : 'suppressed'}
                                        </span>
                                    )}
                                </div>

                                {editorRuleParam && (
                                    <div className="customizations-editor__meta">
                                        <span className="customizations-pill">Parameter</span>
                                        <span>{editorRuleParam.name}</span>
                                        <span className="customizations-pill">{editorRuleParam.type}</span>
                                    </div>
                                )}

                                {isFlowSuppressionRule(editor.row) && (
                                    <label className="customizations-field">
                                        <span>Status</span>
                                        <select value={editor.active ? 'true' : 'false'} onChange={e => setEditor(prev => prev ? { ...prev, active: e.target.value === 'true' } : prev)}>
                                            <option value="true">active</option>
                                            <option value="false">suppressed</option>
                                        </select>
                                    </label>
                                )}

                                {isFlowSuppressionRule(editor.row) && (
                                    <div className="customizations-editor__scopeSection">
                                        <RuleScopeMultiSelect
                                            label="Suppress on"
                                            placeholder="Select systems"
                                            options={ruleScopeOptions}
                                            value={editor.scopes || (editor.scope ? [editor.scope] : [])}
                                            onChange={scopes => setEditor(prev => prev ? { ...prev, scopes } : prev)}
                                        />
                                        <small className="customizations-field__hint">
                                            Choose the systems where this flow suppression should apply.
                                        </small>
                                    </div>
                                )}

                                {!isFlowSuppressionRule(editor.row) && editorRuleParam && (
                                    <div className="customizations-scope-editor">
                                        <div className="customizations-scope-editor__section customizations-scope-editor__section--fallback">
                                            <div className="customizations-scope-editor__sectionHeader">
                                                <span className="customizations-pill">All systems (*)</span>
                                            </div>
                                            <div className="customizations-scope-editor__valueRow">
                                                {getRuleParamEditorControl(
                                                    editorRuleParam.type,
                                                    editor.params || '',
                                                    nextValue => setEditor(prev => prev ? { ...prev, params: nextValue } : prev),
                                                    `Enter ${editorRuleParam.name} value`,
                                                )}
                                            </div>
                                        </div>

                                        <div className="customizations-scope-editor__section">
                                            <div className="customizations-scope-editor__sectionHeader">
                                                <span className="customizations-scope-editor__sectionTitle">System-specific overrides</span>
                                                <button type="button" className="secondary small" onClick={addEditorRuleScopeEntry}>
                                                    Add system
                                                </button>
                                            </div>
                                            {editorRuleScopeEntries.length === 0 ? (
                                                <small className="customizations-field__hint">
                                                    No system-specific override yet. Add one if this value should differ from the fallback.
                                                </small>
                                            ) : (
                                                <div className="customizations-scope-editor__rows">
                                                    {editorRuleScopeEntries.map((entry, index) => {
                                                        const usedScopes = new Set(editorRuleScopeEntries.map(item => item.scope).filter(Boolean))
                                                        return (
                                                            <div key={`${entry.scope}-${index}`} className="customizations-scope-editor__row">
                                                                <select
                                                                    value={entry.scope}
                                                                    onChange={event => updateEditorRuleScopeEntry(index, { scope: event.target.value })}
                                                                >
                                                                    {ruleScopeOptions
                                                                        .filter(option => option.value !== ALL_SYSTEMS_SCOPE || entry.scope === ALL_SYSTEMS_SCOPE)
                                                                        .map(option => (
                                                                            <option
                                                                                key={option.value}
                                                                                value={option.value}
                                                                                disabled={option.value !== entry.scope && usedScopes.has(option.value)}
                                                                            >
                                                                                {option.label}
                                                                            </option>
                                                                        ))}
                                                                </select>
                                                                <div className="customizations-scope-editor__rowValue">
                                                                    {getRuleParamEditorControl(
                                                                        editorRuleParam.type,
                                                                        entry.value,
                                                                        nextValue => updateEditorRuleScopeEntry(index, { value: nextValue }),
                                                                        `Enter ${editorRuleParam.name} value`,
                                                                    )}
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className="secondary small"
                                                                    onClick={() => removeEditorRuleScopeEntry(index)}
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        <small className="customizations-field__hint">
                                            Edit the fallback value for <code>{editorRuleParam.name}</code> and override it per system when needed.
                                        </small>
                                    </div>
                                )}
                            </>
                        )}

                        {editorError && (
                            <div className="customizations-editor__error" role="alert">
                                {editorError}
                            </div>
                        )}

                            <div className={`customizations-editor__actions${editor.mode === 'delete' ? ' customizations-editor__actions--delete' : ''}`}>
                                {editor.mode === 'delete' ? (
                                    <>
                                        <button
                                            className="customizations-panel__remove-btn"
                                            onClick={saveEditor}
                                            disabled={isSubmitting || !editor.deleteReason?.trim()}
                                        >
                                            {editor.kind === 'etl' ? etlDeleteLabel : ruleDeleteLabel}
                                        </button>
                                        {isSubmitting && <span className="customizations-editor__saving-indicator"><Spinner isSmall={true} /></span>}
                                        <div className="customizations-editor__action-group">
                                            <button className="secondary" onClick={closeEditor} disabled={isSubmitting}>Cancel</button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {isSubmitting && <span className="customizations-editor__saving-indicator"><Spinner isSmall={true} /></span>}
                                        <div className="customizations-editor__action-group">
                                            <button className="secondary" onClick={closeEditor} disabled={isSubmitting}>Cancel</button>
                                            {!isSubmitting && <button className="primary" onClick={saveEditor}>Save override</button>}
                                        </div>
                                    </>
                                )}
                            </div>
                    </div>
                </SlideDrawer>
            )}

        </div>
    )
}
